import {
    getVerificationStatusKeyAndFlag,
    KEY_APPROVED, KEY_NONE,
    KEY_NULL,
    KEY_PROGRESS, PROCESSING,
} from "../utils/getVerificationStatusKey";
import { isVersionUK, isVersionUS } from "../services/versionService";
import { getPrimaryAddress, getPrimaryPhone } from "../utils/getPrimaryData";

export const KUS_SCHOOL = "KUS";
export const FULL_TIME = "Full Time";

export const applicantStatusHelper = {
    hasContactFullInfo(contact) {
        return !!contact?.forename && !!contact?.surname && !!contact?.dateOfBirth && !!contact?.emailAddress;
    },

    hasContactFullDetails(contact) {
        const primaryAddress = getPrimaryAddress(contact.addresses);
        const primaryPhone = getPrimaryPhone(contact.telephoneNumbers);

        return !!contact?.forename && !!contact?.surname && !!contact?.dateOfBirth && !!contact?.emailAddress
            && !!primaryPhone?.countryCode && !!primaryPhone?.number
            && !!primaryAddress?.addressLine1 && !!primaryAddress?.city && !!primaryAddress?.code && !!primaryAddress?.country;
    },

    hideSubjectSelectionUS(enrolment, student) {
        return isVersionUS && enrolment?.school === KUS_SCHOOL && student?.studySchedule === FULL_TIME;
    },

    showConsultationCall(hideSubjectSelectionUS, student) {
        if (isVersionUS) return !hideSubjectSelectionUS;

        return !!student?.studyingIb || !!student?.hasSafeguardingNeed || [11, 13].includes(student?.yearGroup);
    },

    isRequiredDBS(student) {
        if (isVersionUS) return false;

        return student?.ageAtRegistration && student.ageAtRegistration >= 18;
    },

    isRequiredOutOfYear(student) {
        if (isVersionUS) return false;

        return !!student?.yearGroupForAge && !!student?.yearGroup && Math.abs(student.yearGroupForAge - student.yearGroup) > 1;
    },

    isSchoolReportsRequired(student) {
        if (isVersionUS) return false;

        return [11, 13].includes(student?.yearGroup) || !!student?.studyingIb;
    },

    consultationCallStatus(enrolment, showConsultationCall) {
        const { consultationCallStatus } = enrolment || {};
        if (!showConsultationCall) return { key: KEY_APPROVED };
        if (consultationCallStatus === "Scheduled") return { key: "progress", statusTextKey: "status.scheduled", isInfo: true };
        if (consultationCallStatus === "Complete") return { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true };
        return { key: KEY_NULL, statusTextKey: "status.incomplete", isWarning: true };
    },

    calculateApplicantStatuses: (options) => {
        const {
            hasContactFullInfo,
            primaryContactVerificationKey,
            generalStatuses,
            studentVerificationKey,
            hideSubjectSelectionUS,
            showConsultationCall,
            isSchoolReportsRequired,
            isRequiredDBS,
            isRequiredOutOfYear,
            hasSafeguardingNeed,
            studyingIb,
        } = options;

        const transcriptsUploadStatus = isSchoolReportsRequired || isVersionUS
            ? getVerificationStatusKeyAndFlag({ verificationStatus: generalStatuses.transcriptUploadStatus })
            : { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true };

        const dbsUploadStatus = isRequiredDBS
            ? getVerificationStatusKeyAndFlag({ verificationStatus: generalStatuses.dbsUploadStatus })
            : { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true };

        const outOfYearFormUploadStatus = isRequiredOutOfYear
            ? getVerificationStatusKeyAndFlag({ verificationStatus: generalStatuses.outOfYearFormUploadStatus })
            : { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true };

        const safeguardingUploadStatus = hasSafeguardingNeed
            ? getVerificationStatusKeyAndFlag({ verificationStatus: generalStatuses.safeguardingUploadStatus })
            : { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true };

        const ibApplicationLetter = isVersionUK && !!studyingIb
            ? getVerificationStatusKeyAndFlag({ verificationStatus: generalStatuses.ibApplicationLetterUploadStatus })
            : { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true };

        const ibGradeEvidence = isVersionUK && !!studyingIb
            ? getVerificationStatusKeyAndFlag({ verificationStatus: generalStatuses.ibGradeEvidenceUploadStatus })
            : { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true };

        let consultationCall = showConsultationCall
            ? { key: KEY_NULL, statusTextKey: "status.incomplete", isWarning: true }
            : { key: KEY_APPROVED };

        if (generalStatuses.consultationCallStatus === "Scheduled") {
            consultationCall = { key: KEY_PROGRESS, statusTextKey: "status.scheduled", isInfo: true };
        }
        if (generalStatuses.consultationCallStatus === "Complete") {
            consultationCall = { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true };
        }

        return {
            responsibleAdultsDetails: hasContactFullInfo
                ? { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true }
                : { key: KEY_NULL, statusTextKey: "status.incomplete", isWarning: true },
            responsibleAdults: primaryContactVerificationKey,
            studentAddress: generalStatuses.studentAddressesAdded
                ? { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true }
                : { key: KEY_NULL, statusTextKey: "status.incomplete", isWarning: true },
            studentVerification: studentVerificationKey,
            dbsUpload: dbsUploadStatus,
            outOfYearFormUpload: outOfYearFormUploadStatus,
            safeguardingUpload: safeguardingUploadStatus,
            transcripts: transcriptsUploadStatus,
            previousEducation: generalStatuses.previousEducationAdded && (transcriptsUploadStatus.key !== KEY_NULL ? transcriptsUploadStatus : generalStatuses.previousEducationAdded)
                ? { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true }
                : { key: KEY_NULL, statusTextKey: "status.incomplete", isWarning: true },
            ibApplicationLetter,
            ibGradeEvidence,
            enrolmentAgreement: generalStatuses.contractSigned
                ? { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true }
                : { key: KEY_NULL, statusTextKey: "status.incomplete", isWarning: true },
            courseSelection: !hideSubjectSelectionUS && !generalStatuses.subjectsReviewed
                ? { key: KEY_NULL, statusTextKey: "status.incomplete", isWarning: true }
                : { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true },
            consultationCall,
            tuitionBilling: generalStatuses.tuitionPaymentStatus === PROCESSING
                ? { key: KEY_PROGRESS, statusTextKey: "status.processing", isInfo: true }
                : (generalStatuses.tuitionPaid
                    ? { key: KEY_APPROVED, statusTextKey: "status.complete", isSuccess: true }
                    : { key: KEY_NULL, statusTextKey: "status.incomplete", isWarning: true }),
        };
    },

    checkAdultsAndDetailsAndEducationComplete: (statuses) => {
        if (isVersionUK) {
            return statuses.responsibleAdultsDetails.key === KEY_APPROVED
                && statuses.responsibleAdults.key === KEY_APPROVED
                && statuses.studentAddress.key === KEY_APPROVED
                && statuses.studentVerification.key !== KEY_NONE
                && statuses.dbsUpload.key !== KEY_NONE
                && statuses.outOfYearFormUpload.key === KEY_APPROVED
                && statuses.safeguardingUpload.key !== KEY_NONE
                && statuses.transcripts.key !== KEY_NONE
                && statuses.previousEducation.key === KEY_APPROVED
                && statuses.ibApplicationLetter.key !== KEY_NONE
                && statuses.ibGradeEvidence.key !== KEY_NONE;
        }

        return statuses.responsibleAdultsDetails.key === KEY_APPROVED
            && statuses.responsibleAdults.key === KEY_APPROVED
            && statuses.studentAddress.key === KEY_APPROVED
            && statuses.studentVerification.key === KEY_APPROVED
            && statuses.dbsUpload.key !== KEY_NONE
            && statuses.outOfYearFormUpload.key === KEY_APPROVED
            && statuses.safeguardingUpload.key !== KEY_NONE
            && statuses.transcripts.key !== KEY_NONE
            && statuses.previousEducation.key === KEY_APPROVED
            && statuses.ibApplicationLetter.key !== KEY_NONE
            && statuses.ibGradeEvidence.key !== KEY_NONE;
    },

    calculateLockedStatus: ({
        statuses,
        generalStatuses,
        hideSubjectSelectionUS,
        isAdultsAndDetailsAndEducationComplete,
        showConsultationCall,
    }) => {
        if (isVersionUK) {
            const isEnrolmentLocked = !!generalStatuses?.student?.enrolmentDetailsLocked;

            return {
                responsibleAdults: isEnrolmentLocked,
                studentDetails: isEnrolmentLocked,
                previousEducation: isEnrolmentLocked,
                enrolmentAgreement: !isEnrolmentLocked || (showConsultationCall ? statuses.consultationCall.key !== KEY_APPROVED : false),
                courseSelection: !isEnrolmentLocked,
                consultationCall: !isAdultsAndDetailsAndEducationComplete,
                tuitionBilling: !isEnrolmentLocked
                    || statuses.enrolmentAgreement.key !== KEY_APPROVED
                    || (showConsultationCall ? statuses.consultationCall.key !== KEY_APPROVED : false),
                // || (statuses.consultationCall.key !== KEY_NULL ? statuses.consultationCall.key !== KEY_APPROVED : false),
            };
        }

        // US Version logic
        const isFinished = statuses.enrolmentAgreement.isSuccess && statuses.tuitionBilling.isSuccess
            && (hideSubjectSelectionUS ? true : statuses.courseSelection.isSuccess && statuses.consultationCall.isSuccess);

        const courseSelection = hideSubjectSelectionUS ? true : !isAdultsAndDetailsAndEducationComplete;
        const consultationCall = hideSubjectSelectionUS ? true : !isAdultsAndDetailsAndEducationComplete;
        const enrolmentAgreement = !isAdultsAndDetailsAndEducationComplete;

        const tuitionBilling = hideSubjectSelectionUS
            ? !isAdultsAndDetailsAndEducationComplete
            : courseSelection || consultationCall || statuses.courseSelection.key !== KEY_APPROVED
            || (statuses.consultationCall.key !== KEY_NULL ? statuses.consultationCall.key !== KEY_APPROVED : false);

        return {
            responsibleAdults: isFinished,
            studentDetails: isFinished,
            previousEducation: isFinished,
            enrolmentAgreement,
            courseSelection,
            consultationCall,
            tuitionBilling,
        };
    },

    calculateStepsProgress: (statuses) => ({
        responsibleAdults: {
            completed: statuses.responsibleAdultsDetails?.key === KEY_APPROVED && statuses.responsibleAdults?.key === KEY_APPROVED,
            active: statuses.responsibleAdultsDetails?.key !== KEY_NULL || statuses.responsibleAdults?.key !== KEY_NULL,
        },
        studentDetails: {
            completed: statuses.studentAddress?.key === KEY_APPROVED
                    && statuses.studentVerification?.key === KEY_APPROVED
                    && statuses.outOfYearFormUpload?.key === KEY_APPROVED
                    && statuses.dbsUpload?.key === KEY_APPROVED
                    && statuses.safeguardingUpload?.key === KEY_APPROVED,
            active: statuses.studentAddress?.key !== KEY_NULL
                    || statuses.studentVerification?.key !== KEY_NULL
                    || statuses.outOfYearFormUpload?.key !== KEY_NULL
                    || statuses.dbsUpload?.key !== KEY_NULL
                    || statuses.safeguardingUpload?.key !== KEY_NULL,
        },
        previousEducation: {
            completed: statuses.previousEducation?.key === KEY_APPROVED
                    && statuses.transcripts?.key === KEY_APPROVED
                    && statuses.ibApplicationLetter?.key === KEY_APPROVED
                    && statuses.ibGradeEvidence?.key === KEY_APPROVED,
            active: statuses.previousEducation?.key !== KEY_NULL
                    || statuses.transcripts?.key !== KEY_NULL
                    || statuses.ibApplicationLetter?.key !== KEY_NULL
                    || statuses.ibGradeEvidence?.key !== KEY_NULL,
        },
        enrolmentAgreement: {
            completed: statuses.enrolmentAgreement?.key === KEY_APPROVED,
            active: statuses.enrolmentAgreement?.key !== KEY_NULL,
        },
        courseSelection: {
            completed: statuses.consultationCall?.key === KEY_NULL
                ? statuses.courseSelection?.key === KEY_APPROVED
                : statuses.courseSelection?.key === KEY_APPROVED && statuses.consultationCall?.key === KEY_APPROVED,
            active: statuses.courseSelection?.key !== KEY_NULL || statuses.consultationCall?.key === KEY_NULL,
        },
        tuitionBilling: {
            completed: statuses.tuitionBilling?.key === KEY_APPROVED,
            active: statuses.tuitionBilling?.key !== KEY_NULL,
        },
    }),
};
